/**
 * Auth Layout (SCR-001)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-001 Login - Split layout wrapper
 * @iso27001 A.9.4.2 Secure log-on procedures
 */

import { Outlet, Navigate, Link } from 'react-router-dom';
import { useAuth } from '@myvault/shared/hooks';
import { Spinner } from '@myvault/shared/components';
import { Icon } from '@myvault/shared/components';

const features = [
  { icon: 'shield' as const, text: '256-bit encryptie' },
  { icon: 'sparkles' as const, text: 'AI-gestuurde documentanalyse' },
  { icon: 'userGroup' as const, text: 'Veilig delen met gemachtigden' },
  { icon: 'lock' as const, text: 'GDPR-compliant' },
];

export function AuthLayout() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen">
      {/* Left: Branding Panel (Desktop only) */}
      <div className="hidden w-1/2 flex-col justify-between bg-gradient-to-br from-blue-900 to-blue-800 p-16 lg:flex">
        {/* Logo */}
        <div>
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500">
              <span className="text-lg font-bold text-blue-900">mV</span>
            </div>
            <span className="text-2xl font-semibold text-white">myVault</span>
          </Link>
        </div>

        {/* Main Content */}
        <div className="max-w-md">
          <h1 className="mb-6 text-4xl font-bold leading-tight text-white">
            Jouw documenten, veilig bewaard
          </h1>
          <p className="mb-12 text-lg text-blue-100">
            myVault helpt je al je belangrijke documenten veilig op te slaan, te begrijpen met AI, en te delen met wie je vertrouwt.
          </p>

          {/* Feature List */}
          <ul className="space-y-4">
            {features.map((feature) => (
              <li key={feature.text} className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-700/50">
                  <Icon name={feature.icon} variant="solid" size="sm" className="text-amber-400" />
                </div>
                <span className="text-white">{feature.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="text-sm text-blue-200">
          <p>&copy; {new Date().getFullYear()} myVault. Alle rechten voorbehouden.</p>
        </div>
      </div>

      {/* Right: Form Panel */}
      <div className="flex w-full flex-col bg-gray-50 lg:w-1/2">
        {/* Mobile Header */}
        <header className="flex items-center justify-center py-8 lg:hidden">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500">
              <span className="font-bold text-blue-900">mV</span>
            </div>
            <span className="text-xl font-semibold text-blue-900">myVault</span>
          </Link>
        </header>

        {/* Form Container */}
        <main className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12">
          <div className="w-full max-w-md">
            <Outlet />
          </div>
        </main>

        {/* Mobile Footer */}
        <footer className="py-6 text-center text-sm text-gray-500 lg:hidden">
          <p>&copy; {new Date().getFullYear()} myVault. Alle rechten voorbehouden.</p>
        </footer>
      </div>
    </div>
  );
}
